import { giphyBlue, giphyPink } from '@giphy/colors'
import { IUser } from '@giphy/js-types'
import { useContext, type ReactNode } from 'react'
import { ILoggedInUser } from 'types'
import { HeaderContext } from '../header'
import MenuComponent from './menu'
import UserMenu from './user-menu'

export type UserMenuItem = {
    path: string
    label: string
    permission?: string
}

export type MainMenuLink = {
    title: string
    link: string
    newPill?: boolean
}
export type MainMenuItem = {
    header: MainMenuLink
    items: (MainMenuLink | string)[]
}

export type MobileMenuCopy = {
    loggedOutUser: {
        login: string
        join: string
        favorites: string
    }
    user: {
        favorites: string
        myChannel: string
        settings: string
        upgradeAccount: string
        logOut: string
        dashboard: string
    }
    main: {
        advertise: string
        caseStudies: string
        workWithUs: string
        categories: string
        entertainment: string
        sports: string
        newsAndPolitics: string
        anime: string
        memes: string
        holidays: string
        gaming: string
        emotions: string
        reactions: string
        weird: string
        about: string
        downloadTheApp: string
        communityGuidelines: string
        upgradeAccount: string
        transparency: string
        supportAndFaq: string
        hireAGifArtist: string
        dmca: string
        jobs: string
    }
}

export const DEFAULT_MOBILE_MENU_COPY: MobileMenuCopy = {
    loggedOutUser: {
        login: 'Login',
        join: 'Join',
        favorites: 'Favorites',
    },
    user: {
        favorites: 'Favorites',
        myChannel: 'My Channel',
        settings: 'Settings',
        upgradeAccount: 'Upgrade Account',
        logOut: 'Log Out',
        dashboard: 'Dashboard',
    },
    main: {
        advertise: 'Advertise',
        caseStudies: 'Case Studies',
        workWithUs: 'Work with Us',
        categories: 'Categories',
        entertainment: 'Entertainment',
        sports: 'Sports',
        newsAndPolitics: 'News & Politics',
        anime: 'Anime',
        memes: 'Memes',
        holidays: 'Holidays',
        gaming: 'Gaming',
        emotions: 'Emotions',
        reactions: 'Reactions',
        weird: 'Weird',
        about: 'About',
        downloadTheApp: 'Download the App',
        communityGuidelines: 'Community Guidelines',
        upgradeAccount: 'Upgrade Account',
        transparency: 'Transparency',
        supportAndFaq: 'Support & FAQ',
        hireAGifArtist: 'Hire a GIF Artist',
        dmca: 'DMCA',
        jobs: 'Jobs',
    },
}

type Props = { onItemClick: (path: string) => void; footer?: ReactNode; mobileMenuCopy?: MobileMenuCopy }
const Menu = ({ onItemClick, footer, mobileMenuCopy = DEFAULT_MOBILE_MENU_COPY }: Props) => {
    const { menu, user } = useContext(HeaderContext)
    switch (menu) {
        case 'logged out user':
            return <UserMenu color={giphyBlue} items={loggedOutUserItems(mobileMenuCopy)} itemClick={onItemClick} />
        case 'user':
            return user ? (
                <UserMenu color={giphyBlue} items={userMenuItems(user, mobileMenuCopy)} itemClick={onItemClick} />
            ) : null
        case 'category':
            return (
                <MenuComponent
                    color={giphyPink}
                    items={mainMenuItems(user ?? undefined, mobileMenuCopy)}
                    footer={footer}
                />
            )
        default:
            return null
    }
}

export const loggedOutUserItems = (copy: MobileMenuCopy = DEFAULT_MOBILE_MENU_COPY): UserMenuItem[] => [
    { path: '/login', label: copy.loggedOutUser.login },
    { path: '/join', label: copy.loggedOutUser.join },
    { path: '/favorites', label: copy.loggedOutUser.favorites },
]

export const userMenuItems = (user: IUser, copy: MobileMenuCopy = DEFAULT_MOBILE_MENU_COPY): UserMenuItem[] => {
    const loggedInUser = user as ILoggedInUser
    const { channel, permissions, is_upgraded } = loggedInUser
    const items: UserMenuItem[] = [
        {
            path: '/favorites',
            label: copy.user.favorites,
        },
        {
            path: channel,
            label: copy.user.myChannel,
        },
        {
            path: '/settings',
            label: copy.user.settings,
        },
        ...(!is_upgraded ? [{ path: '/apply', label: copy.user.upgradeAccount }] : []),
        {
            path: '/logout',
            label: copy.user.logOut,
        },
    ]

    const gatedItems = [
        {
            path: '/dashboard',
            label: copy.user.dashboard,
            permission: 'dashboard_analytics',
        },
    ]

    // @ts-ignore TODO type this
    const filteredGatedItems = gatedItems.filter((item) => permissions[item.permission])
    return [items[0], ...filteredGatedItems, ...items.slice(1)]
}

const getItem =
    (prefix: string, joiner = '') =>
    (title: string | object): MainMenuLink | string =>
        title instanceof Object
            ? (title as unknown as string)
            : {
                  title,
                  link: `${prefix}${encodeURIComponent(title.replace(' ', joiner || ''))}`.toLowerCase(),
              }

export const mainMenuItems = (user?: IUser, copy: MobileMenuCopy = DEFAULT_MOBILE_MENU_COPY): MainMenuItem[] => [
    {
        header: { title: copy.main.advertise, link: 'https://ads.giphy.com' },
        items: [
            {
                title: copy.main.caseStudies,
                link: 'https://ads.giphy.com#comp-m862ifvu',
            },
            {
                title: copy.main.workWithUs,
                link: 'https://ads.giphy.com',
            },
        ],
    },
    {
        header: { title: copy.main.categories, link: '/categories' },
        items: [
            { title: copy.main.entertainment, link: `/categories/entertainment` },
            { title: copy.main.sports, link: `/categories/sports` },
            { title: copy.main.newsAndPolitics, link: `/categories/news-politics` },
            { title: copy.main.anime, link: `/categories/anime` },
            { title: copy.main.memes, link: `/categories/memes` },
            { title: copy.main.holidays, link: `/categories/holidays` },
            { title: copy.main.gaming, link: `/categories/gaming` },
            { title: copy.main.emotions, link: `/categories/emotions` },
            { title: copy.main.reactions, link: `/categories/reactions` },
            { title: copy.main.weird, link: `/categories/weird` },
        ],
    },
    {
        header: { title: copy.main.about, link: '/about' },
        items: [
            {
                title: copy.main.downloadTheApp,
                link: 'https://apps.apple.com/us/app/giphy-the-gif-search-engine/id974748812',
            },
            {
                title: copy.main.communityGuidelines,
                link: '/community-guidelines',
            },
            ...(!user || !(user as ILoggedInUser).is_upgraded
                ? [
                      {
                          title: copy.main.upgradeAccount,
                          link: '/apply',
                      },
                  ]
                : []),
            {
                title: copy.main.transparency,
                link: '/transparency/2025',
            },
            {
                title: copy.main.supportAndFaq,
                link: 'https://support.giphy.com/hc/en-us',
            },
            {
                title: copy.main.hireAGifArtist,
                link: 'https://support.giphy.com/hc/en-us/articles/360022661172-How-to-Hire-an-Artist-on-GIPHY',
            },
            {
                title: copy.main.dmca,
                link: 'https://support.giphy.com/hc/en-us/articles/360020287091-GIPHY-DMCA-Copyright-Policy',
            },
            {
                title: copy.main.jobs,
                link: 'https://careers.shutterstock.com/us/en/search-results?keywords=giphy',
            },
        ].map(getItem('/')),
    },
]

export default Menu
